import { apiv2_createUserTicket } from "@/app/api2"
import { getEscHandler } from "@/app/esc_handler"
import { useUserStore } from "@/app/providers/user_provider"
import { eventTracker } from "@/app/types"
import { Notifier } from "@/app/types/notifier"
import { Button } from "@/components/buttons/button"
import { MessagePopup } from "@/components/popups/popup.message"
import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

interface FeedbackPopupProps {
  isOpen: boolean
  close: () => void
}

interface FormProps {
  isPositive: boolean | null
  description?: string
}

export function FeedbackPopup({ isOpen, close }: FeedbackPopupProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const [isMessagePopupOpen, setIsMessagePopupOpen] = useState(false)

  const notifier = new Notifier()

  const isLoggedIn = useUserStore(useShallow((store) => store.isLoggedIn))

  const {
    register,
    formState: { isSubmitting },
    handleSubmit,
    control,
    reset,
  } = useForm<FormProps>({
    defaultValues: {
      isPositive: null,
      description: "",
    },
  })

  // Handle outside clicks
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        close()
        reset()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick)
      // Prevent body scrolling
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      document.body.style.overflow = ""
    }
  }, [isOpen, close, reset])

  useEffect(getEscHandler(() => close()))

  // don't render if not open
  if (!isOpen) return null

  async function onSubmit(data: FormProps) {
    let feedbackType = "NONE"

    if (data.isPositive === true) {
      feedbackType = "POSITIVE"
    } else if (data.isPositive === false) {
      feedbackType = "NEGATIVE"
    }

    if (!isLoggedIn) {
      feedbackType = `PUBLIC: ${feedbackType}`
    }

    const feedbackBody = {
      description: `${feedbackType}: ${data.description}`,
    }

    try {
      const response = await apiv2_createUserTicket(feedbackBody, isLoggedIn)
      if (response) {
        eventTracker.trackEvent({
          event: "app:feedback_send",
        })
        setIsMessagePopupOpen(true)
        reset()
        close()
      }
    } catch (error) {
      notifier.error(`Error: ${error.message} `)
    }
  }

  return (
    <>
      <MessagePopup
        title="We are happy to hear from you!"
        message="We appreciate the time you took to give us your opinion. Your feedback will be carefully analyzed by our team."
        isOpen={isMessagePopupOpen}
        close={() => setIsMessagePopupOpen(false)}
      />

      <div className="fixed inset-0 z-10" role="dialog" aria-modal="true">
        <div className="fixed inset-0 bg-main-700/50" aria-hidden="true" />
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div
              ref={modalRef}
              className="relative -translate-y-1/4 transform rounded-3xl border border-main-400 bg-main-light p-3 text-left xl:-right-[120px]"
              onClick={(e) => e.stopPropagation()}>
              <div className="px-2 pt-2">
                <div>
                  <div>
                    <h2 className="text-base font-semibold text-main-dark">
                      Feedback
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-main-600">
                      Let us know what you think. Your feedback is important to
                      us!
                    </p>
                  </div>
                </div>
                <div className="w-full border-b border-main-900/10 pb-3"></div>
                <main className="pt-3">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-12">
                      <div className="grid grid-cols-1 gap-x-8 gap-y-5 pb-12">
                        <div className="max-w-2xl space-y-5 md:col-span-2">
                          <div className="col-span-full">
                            <textarea
                              rows={3}
                              autoFocus={true}
                              className="block h-32 w-full resize-none rounded-md border border-main-400 px-1.5 py-1.5 text-xs text-main-900 shadow-sm placeholder:text-main-400"
                              placeholder="Comments & Suggestions"
                              {...register("description")}
                            />
                          </div>
                          {/* ID, 11-June-25 - commented out based on discussion in Dev Status call. Will bring back in later.
                          {isLoggedIn && (
                            <fieldset>
                              <legend className="text-base font-semibold text-main-dark">
                                Would you recommend Grafiniti?
                              </legend>
                              <div className="mt-4 flex items-center gap-2 px-0 sm:px-1">
                                <Controller
                                  control={control}
                                  name="isPositive"
                                  render={({ field }) => (
                                    <div className="flex items-center gap-2 px-0 sm:px-1">
                                      <div className="relative flex items-center justify-center">
                                        <button
                                          type="button"
                                          className={`h-8 w-16 cursor-pointer rounded-full border border-main-300 ${field.value === true ? "bg-main-trend-up" : "bg-main-200"}`}
                                          onClick={() => field.onChange(true)}
                                        />
                                        <div className="pointer-events-none absolute">
                                          <ThumbsUp
                                            className={`w-5 ${field.value !== true ? "text-main-dark" : "text-main-light"}`}
                                          />
                                        </div>
                                      </div>
                                      <div className="relative flex items-center justify-center">
                                        <button
                                          type="button"
                                          className={`h-8 w-16 cursor-pointer rounded-full border border-main-300 ${field.value === false ? "bg-main-trend-down" : "bg-main-200"}`}
                                          onClick={() => field.onChange(false)}
                                        />
                                        <div className="pointer-events-none absolute">
                                          <ThumbsDown
                                            className={`w-5 ${field.value !== false ? "text-main-dark" : "text-main-light"}`}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                />
                              </div>
                            </fieldset>
                          )} */}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-start gap-x-2 px-1">
                      <Button
                        disabled={isSubmitting}
                        className="!mx-0 text-xs disabled:cursor-not-allowed disabled:bg-main-200 disabled:text-gray-400"
                        color="primary"
                        type="submit">
                        Submit
                      </Button>
                      <Button
                        onClick={() => {
                          close()
                          reset()
                        }}
                        className="!mx-0 text-xs"
                        color="tertiary"
                        type="button">
                        Close
                      </Button>
                    </div>
                  </form>
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
